/**
 * Touch-first packing controls for transfer pre-pack.
 */

import { useEffect, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Loader2, Pencil, Printer, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { useDisabledTapToast } from "@/components/ui/TapDisabledReason";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import type { TransferPackAllocation, TransferPackBox } from "./prepackApi";
import { transferPackContainerIdentityLabel } from "./transferPackQuantityScan";
import { MrpSkuWithUnitLabel } from "@/components/mrp/MrpSkuWithUnitLabel";
import type { MrpSkuHoverHintFields } from "@/lib/mrp";
import { EntityPeekHint } from "@/components/hub/EntityPeekHint";
import { HoverHintContent, HoverHintWithGlyph } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import { transferLinePackQuantityBadge } from "@/pages/manufacturing/transfersWorkbenchTypes";
import {
  transferPackNumpadFillClasses,
  numpadActivateFromPointerDown,
  type TransferPackNumpadFillFrom,
} from "./prepackTransferKioskLayout";

import type { TransferPackLine } from "./prepackApi";
import type { TransferPackWorkspace } from "./prepackApi";
import { TRANSFER_ORIGIN_ON_HAND_HOVER_HINT } from "@/pages/manufacturing/transfersOriginInventoryModel";

export type TransferPackInputMode = "scan" | "quantity";

export type TransferPackNumpadSide = "left" | "right";

export const PREPACK_TRANSFER_NUMPAD_SIDE_STORAGE_KEY = "prepack-transfer-numpad-side";

export function readStoredTransferPackNumpadSide(): TransferPackNumpadSide {
  try {
    const stored = window.localStorage.getItem(PREPACK_TRANSFER_NUMPAD_SIDE_STORAGE_KEY);
    if (stored === "left" || stored === "right") return stored;
  } catch {
    /* ignore */
  }
  return "right";
}

/** Hub header + main padding — tuned for 1080p with browser chrome visible. */
export const PREPACK_TRANSFER_KIOSK_HEIGHT_CLASS = "h-[calc(100dvh-7rem)]";

export {
  kioskMainPaneOrderClass,
  kioskNumpadPaneOrderClass,
  kioskNumpadSplitGridClass,
  kittingPickNumpadOnBottom,
  kittingPickNumpadOnRight,
  kittingPickNumpadSplitGridClass,
  KITTING_PICK_NUMPAD_CORNER_STORAGE_KEY,
  readStoredKittingPickNumpadCorner,
  transferPackNumpadFillClasses,
} from "./prepackTransferKioskLayout";
export type { KittingPickNumpadCorner, TransferPackNumpadFillFrom } from "./prepackTransferKioskLayout";

function kioskSkuCategory(hint: MrpSkuHoverHintFields | undefined): string | null {
  const category = hint?.category?.trim();
  return category != null && category !== "" ? category : null;
}

function kioskSkuDescriptionLine(hint: MrpSkuHoverHintFields | undefined): string {
  const parts = [hint?.description?.trim(), hint?.variant?.trim()].filter(
    (part): part is string => part != null && part !== "",
  );
  return parts.length > 0 ? parts.join(" ") : "—";
}

export function TransferPackSkuFields({
  hint,
}: {
  hint: MrpSkuHoverHintFields | undefined;
}): JSX.Element {
  return <EntityPeekHint kind="sku_metadata" fields={hint ?? {}} />;
}

export function projectedTransferSkuPackedTotal(input: {
  lines: readonly TransferPackLine[];
  skuId: string;
  lineId: number;
  lineAllocatedAfter: number;
}): number {
  const skuKey = input.skuId.trim().toLowerCase();
  const otherLines = input.lines
    .filter(
      (line) =>
        line.id !== input.lineId
        && (line.sku_id?.trim().toLowerCase() ?? "") === skuKey,
    )
    .reduce((sum, line) => sum + line.allocated_quantity, 0);
  return otherLines + input.lineAllocatedAfter;
}

export function TransferPackLineTile({
  line,
  hint,
  selected,
  disabled,
  onSelect,
  kiosk = false,
}: {
  line: TransferPackLine;
  hint: MrpSkuHoverHintFields | undefined;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  kiosk?: boolean;
}): JSX.Element {
  const complete = line.unallocated_quantity <= 0;
  const blocked = (disabled || complete || line.sku_id == null) && !selected;
  const packQtyBadge = transferLinePackQuantityBadge(line);
  const skuLabel = line.sku_id ?? "—";
  const kioskCategory = kiosk ? kioskSkuCategory(hint) : null;
  const originOnHand = line.origin_on_hand;
  const stockShort =
    originOnHand != null
    && Number.isFinite(originOnHand)
    && line.allocated_quantity + line.unallocated_quantity > originOnHand;

  return (
    <button
      type="button"
      disabled={blocked}
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={selected ? `Leave ${skuLabel} and scan` : `Select ${skuLabel}`}
      className={cn(
        "flex h-full min-h-0 flex-col self-stretch rounded-lg border-2 text-left transition",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1",
        kiosk
          ? "box-border h-full min-h-[6.75rem] w-full p-2"
          : "box-border h-full min-h-[11rem] w-full p-4",
        selected
          ? "border-brand-500 bg-brand-50 shadow-sm"
          : complete
            ? "border-slate-200 bg-slate-50 opacity-70"
            : "border-slate-300 bg-white hover:border-slate-400 active:bg-slate-50",
        blocked ? "cursor-not-allowed" : "cursor-pointer",
      )}
    >
      <div className="flex items-start justify-between gap-1">
        <p
          className={cn(
            "min-w-0 leading-snug text-slate-900",
            kiosk ? "line-clamp-2 text-sm" : "truncate font-mono text-base font-semibold",
          )}
        >
          {kiosk ? (
            <>
              <MrpSkuWithUnitLabel
                sku={skuLabel}
                unit={hint?.mv_unit}
                className="font-mono font-semibold"
              />
              {kioskCategory != null ? (
                <span className="text-[11px] font-normal text-slate-700"> ({kioskCategory})</span>
              ) : null}
            </>
          ) : (
            <MrpSkuWithUnitLabel sku={skuLabel} unit={hint?.mv_unit} />
          )}
        </p>
        {packQtyBadge === "discrepancy" ? (
          <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-900">
            Discrepancy
          </span>
        ) : packQtyBadge === "approved" ? (
          <span className="shrink-0 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-900">
            Approved
          </span>
        ) : null}
      </div>
      <div className="mt-1 min-h-0 flex-1 overflow-hidden">
        {kiosk ? (
          <p className="line-clamp-3 text-[11px] leading-snug text-slate-700">
            {kioskSkuDescriptionLine(hint)}
          </p>
        ) : (
          <TransferPackSkuFields hint={hint} />
        )}
      </div>
      <div
        className={cn(
          "mt-auto shrink-0 grid grid-cols-4 gap-0.5 border-t border-slate-200 pt-1 text-center",
          kiosk ? "text-[10px]" : "",
        )}
      >
        <div>
          <p className="font-semibold uppercase tracking-wide text-slate-500">Ord</p>
          <p className={cn("font-semibold tabular-nums text-slate-700", kiosk ? "text-sm" : "text-lg")}>
            {line.quantity}
          </p>
        </div>
        <div>
          <HoverHintContent hoverHint={TRANSFER_ORIGIN_ON_HAND_HOVER_HINT}>
            <p className="font-semibold uppercase tracking-wide text-slate-500">Stock</p>
          </HoverHintContent>
          <p
            className={cn(
              "font-semibold tabular-nums",
              kiosk ? "text-sm" : "text-lg",
              stockShort ? "text-amber-800" : "text-slate-700",
            )}
          >
            {originOnHand == null ? "—" : String(Math.round(originOnHand))}
          </p>
        </div>
        <div>
          <p className="font-semibold uppercase tracking-wide text-slate-500">Packed</p>
          <p className={cn("font-semibold tabular-nums text-slate-700", kiosk ? "text-sm" : "text-lg")}>
            {line.allocated_quantity}
          </p>
        </div>
        <div>
          <p className="font-semibold uppercase tracking-wide text-slate-500">Left</p>
          <p
            className={cn(
              "font-bold tabular-nums",
              kiosk ? "text-lg" : "text-2xl",
              line.unallocated_quantity > 0 ? "text-amber-800" : "text-emerald-700",
            )}
          >
            {line.unallocated_quantity}
          </p>
        </div>
      </div>
    </button>
  );
}

export function numpadDigitFromKeyboardEvent(event: KeyboardEvent | ReactKeyboardEvent): string | null {
  if (/^\d$/.test(event.key)) return event.key;
  const numpad = /^Numpad(\d)$/.exec(event.key);
  return numpad?.[1] ?? null;
}

const NUMPAD_DIGIT_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"] as const;

function numpadActivateProps(disabled: boolean, action: () => void): {
  tabIndex: number;
  onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void;
} {
  return {
    tabIndex: -1,
    onPointerDown: (event) => {
      if (disabled) return;
      numpadActivateFromPointerDown(event, action);
    },
  };
}

export function TransferPackNumpad({
  disabled,
  disabledReason = null,
  onDigit,
  onClear,
  onBackspace,
  onEnter,
  fillHeight = false,
  fillHeightFrom = "lg",
}: {
  disabled: boolean;
  /** When set, a tap on the disabled pad explains why instead of doing nothing. */
  disabledReason?: string | null;
  onDigit: (digit: string) => void;
  onClear: () => void;
  onBackspace: () => void;
  onEnter: () => void;
  fillHeight?: boolean;
  /** Stretch from this breakpoint. Default lg so inbound receive (stacked until lg) keeps intrinsic keys. */
  fillHeightFrom?: TransferPackNumpadFillFrom;
}): JSX.Element {
  const explainDisabled = useDisabledTapToast();
  const fillClasses = fillHeight ? transferPackNumpadFillClasses(fillHeightFrom) : null;
  const buttonClass = fillClasses?.buttonClass ?? "min-h-14 touch-manipulation text-lg font-semibold";
  const digitClass = fillClasses?.digitClass ?? "min-h-14 touch-manipulation text-2xl font-semibold tabular-nums";
  const enterClass = fillClasses?.enterClass ?? "min-h-14 touch-manipulation text-xl font-semibold";
  const tapReason = disabled && disabledReason != null && disabledReason.trim() !== "" ? disabledReason.trim() : null;

  return (
    <div className={cn("relative min-h-0", fillHeight && "h-full")}>
    <div
      data-transfer-pack-numpad
      className={cn(
        "grid grid-cols-3 gap-1.5",
        fillClasses?.gridClass ?? "",
        tapReason != null && "pointer-events-none",
      )}
      onKeyDownCapture={(event) => {
        if (disabled || event.key !== "Enter") return;
        event.preventDefault();
        event.stopPropagation();
        onEnter();
      }}
    >
      {NUMPAD_DIGIT_KEYS.map((key) => (
        <Button
          key={key}
          type="button"
          variant="secondary"
          disabled={disabled}
          className={digitClass}
          {...numpadActivateProps(disabled, () => onDigit(key))}
        >
          {key}
        </Button>
      ))}
      <Button
        type="button"
        variant="secondary"
        disabled={disabled}
        className={buttonClass}
        {...numpadActivateProps(disabled, onClear)}
      >
        Clear
      </Button>
      <Button
        type="button"
        variant="secondary"
        disabled={disabled}
        className={digitClass}
        {...numpadActivateProps(disabled, () => onDigit("0"))}
      >
        0
      </Button>
      <Button
        type="button"
        variant="secondary"
        disabled={disabled}
        className={buttonClass}
        aria-label="Backspace"
        {...numpadActivateProps(disabled, onBackspace)}
      >
        ⌫
      </Button>
      <Button
        type="button"
        variant="primary"
        disabled={disabled}
        className={cn(enterClass, "col-span-3")}
        {...numpadActivateProps(disabled, onEnter)}
      >
        Enter
      </Button>
    </div>
    {tapReason != null ? (
      <button
        type="button"
        className="absolute inset-0 cursor-not-allowed touch-manipulation rounded-md bg-transparent"
        aria-label={tapReason}
        onClick={() => explainDisabled(tapReason)}
      />
    ) : null}
    </div>
  );
}

export function TransferPackNumpadSideToggle({
  side,
  onSideChange,
}: {
  side: TransferPackNumpadSide;
  onSideChange: (side: TransferPackNumpadSide) => void;
}): JSX.Element {
  return (
    <div
      className="flex h-12 shrink-0 rounded-md border border-slate-200 bg-white p-0.5"
      role="group"
      aria-label="Numpad side"
    >
      <Button
        type="button"
        size="sm"
        variant={side === "left" ? "primary" : "secondary"}
        className="h-full min-h-0 min-w-[3.25rem] px-2.5 text-xs font-semibold"
        aria-pressed={side === "left"}
        onClick={() => onSideChange("left")}
      >
        Left
      </Button>
      <Button
        type="button"
        size="sm"
        variant={side === "right" ? "primary" : "secondary"}
        className="h-full min-h-0 min-w-[3.25rem] px-2.5 text-xs font-semibold"
        aria-pressed={side === "right"}
        onClick={() => onSideChange("right")}
      >
        Right
      </Button>
    </div>
  );
}

export type TransferPackCartonQuantityColumn = {
  digitCount: number;
};

/** Shared quantity column width for every line in one carton (right-aligned digits). */
export function resolveTransferPackCartonQuantityColumn(
  allocations: readonly Pick<TransferPackAllocation, "quantity">[],
): TransferPackCartonQuantityColumn {
  const digitCount =
    allocations.length === 0
      ? 1
      : Math.max(...allocations.map((row) => String(row.quantity).length));
  return { digitCount };
}

export function TransferPackCartonAllocationRow({
  allocation,
  hint,
  editing,
  disabled,
  readOnly = false,
  quantityColumn,
  onEdit,
  onDelete,
}: {
  allocation: TransferPackAllocation;
  hint: MrpSkuHoverHintFields | undefined;
  editing: boolean;
  disabled: boolean;
  readOnly?: boolean;
  quantityColumn: TransferPackCartonQuantityColumn;
  onEdit: () => void;
  onDelete: () => void;
}): JSX.Element {
  const skuLabel = allocation.sku_id ?? "—";
  const category = kioskSkuCategory(hint);
  const containerIdentity = transferPackContainerIdentityLabel(allocation.inventory_containers);

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-md border px-2 py-2",
        editing ? "border-brand-500 bg-brand-50" : "border-slate-200 bg-white",
      )}
    >
      <p
        className="shrink-0 text-right text-4xl font-bold tabular-nums leading-none text-slate-900"
        style={{ width: `${String(quantityColumn.digitCount)}ch` }}
        aria-label={`Quantity ${String(allocation.quantity)}`}
      >
        {allocation.quantity}
      </p>
      <div className="min-w-0 flex-1">
        <p className="min-w-0 truncate leading-snug text-slate-900">
          <MrpSkuWithUnitLabel
            sku={skuLabel}
            unit={hint?.mv_unit}
            className="font-mono text-sm font-semibold"
          />
          {category != null ? (
            <span className="text-[11px] font-normal text-slate-700"> ({category})</span>
          ) : null}
        </p>
        <p className="line-clamp-2 text-[11px] leading-snug text-slate-700">
          {kioskSkuDescriptionLine(hint)}
        </p>
        {containerIdentity != null ? (
          <p className="truncate font-mono text-[11px] font-semibold leading-snug text-slate-900">
            {containerIdentity}
          </p>
        ) : null}
      </div>
      {readOnly ? null : (
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            type="button"
            variant="secondary"
            className="h-11 w-11 p-0"
            disabled={disabled}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
            aria-label={`Edit ${skuLabel} quantity in carton`}
          >
            <Pencil className="h-5 w-5" aria-hidden />
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="h-11 w-11 p-0"
            disabled={disabled}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
            }}
            aria-label={`Remove ${skuLabel} from carton`}
          >
            <Trash2 className="h-5 w-5" aria-hidden />
          </Button>
        </div>
      )}
    </li>
  );
}

export function TransferPackCartonsPanel({
  boxes,
  canPack,
  openBox,
  skuHints,
  editingAllocationId,
  quantityMutationPending,
  sealPending,
  printingBoxNumber,
  unsealPendingBoxId,
  showUnseal,
  onSeal,
  onPrintLabel,
  onUnseal,
  onEditAllocation,
  onDeleteAllocation,
  onLaborActivity,
}: {
  boxes: readonly TransferPackBox[];
  canPack: boolean;
  openBox: TransferPackBox | null;
  skuHints: Record<string, MrpSkuHoverHintFields>;
  editingAllocationId: string | null;
  quantityMutationPending: boolean;
  sealPending: boolean;
  printingBoxNumber: number | null;
  unsealPendingBoxId: number | null;
  showUnseal: boolean;
  onSeal: (boxId: number) => void;
  onPrintLabel: (boxNumber: number) => void;
  onUnseal: (boxId: number) => void;
  onEditAllocation: (allocation: TransferPackAllocation) => void;
  onDeleteAllocation: (allocationId: string) => void;
  /** Keeps the labor idle timer alive while scrolling or touching inside this panel. */
  onLaborActivity?: () => void;
}): JSX.Element {
  const [expandedSealedBoxIds, setExpandedSealedBoxIds] = useState<Record<number, boolean>>({});
  const sortedBoxes = [...boxes].sort((left, right) => left.box_number - right.box_number);

  function toggleSealedBoxContents(boxId: number): void {
    setExpandedSealedBoxIds((previous) => ({
      ...previous,
      [boxId]: !previous[boxId],
    }));
  }

  return (
    <div
      className="flex h-full min-h-0 flex-col gap-2 overflow-y-scroll overscroll-y-contain py-1 [touch-action:pan-y] [-webkit-overflow-scrolling:touch]"
      onScroll={onLaborActivity}
      onTouchStart={onLaborActivity}
      onTouchMove={onLaborActivity}
      onPointerDown={onLaborActivity}
    >
      {sortedBoxes.length === 0 && !canPack ? (
        <p className="py-2 text-xs text-slate-500">No cartons yet.</p>
      ) : null}
      {sortedBoxes.map((box) => {
        const isOpen = !box.sealed;
        const isPrimaryOpen = openBox?.id === box.id;
        const sealedContentsExpanded = expandedSealedBoxIds[box.id] === true;
        const quantityColumn = resolveTransferPackCartonQuantityColumn(box.allocations);
        return (
          <div
            key={box.id}
            className={cn(
              "shrink-0 overflow-hidden rounded-lg border",
              isOpen ? "border-brand-300 bg-white" : "border-slate-200 bg-slate-50",
            )}
          >
            <div className="flex items-center gap-2 border-b border-slate-100 px-2 py-1.5">
              <h3 className="min-w-0 flex-1 text-sm font-semibold text-slate-900">
                Carton #{String(box.box_number)}
                {isOpen ? (
                  <span className="font-normal text-brand-700">
                    {" "}
                    · open
                    {isPrimaryOpen && sortedBoxes.some((row) => !row.sealed && row.id !== box.id)
                      ? " (receiving scans)"
                      : ""}
                  </span>
                ) : (
                  <span className="font-normal text-slate-500"> · sealed</span>
                )}
              </h3>
              {box.sealed ? (
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button
                    type="button"
                    variant="secondary"
                    className={cn(
                      "inline-flex min-h-10 items-center gap-1.5 px-3 text-sm font-semibold",
                      box.label_printed_at == null
                        ? "bg-slate-900 text-white hover:bg-slate-800"
                        : "",
                    )}
                    disabled={printingBoxNumber != null || unsealPendingBoxId != null}
                    onClick={() => onPrintLabel(box.box_number)}
                  >
                    {printingBoxNumber === box.box_number ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                    ) : (
                      <Printer className="h-4 w-4 shrink-0" aria-hidden />
                    )}
                    {box.label_printed_at == null ? "Print" : "Reprint"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="min-h-10 px-3 text-sm font-semibold"
                    disabled={box.allocations.length === 0}
                    aria-expanded={sealedContentsExpanded}
                    onClick={() => toggleSealedBoxContents(box.id)}
                  >
                    {sealedContentsExpanded ? "Hide contents" : "View contents"}
                  </Button>
                  {showUnseal ? (
                    <Button
                      type="button"
                      variant="secondary"
                      className="min-h-10 px-3 text-sm font-semibold"
                      disabled={
                        unsealPendingBoxId != null
                        || printingBoxNumber != null
                        || box.allocations.length === 0
                      }
                      onClick={() => onUnseal(box.id)}
                    >
                      {unsealPendingBoxId === box.id ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                      ) : null}
                      Unseal
                    </Button>
                  ) : null}
                </div>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  className="min-h-10 shrink-0 px-4 text-sm font-semibold"
                  disabled={!canPack || box.allocations.length === 0 || sealPending}
                  onClick={() => onSeal(box.id)}
                >
                  Seal carton
                </Button>
              )}
            </div>
            {isOpen || (box.sealed && sealedContentsExpanded) ? (
              <ul
                className={cn(
                  "space-y-1 py-1 pl-2",
                  isOpen ? "border-l-2 border-brand-100" : "border-l-2 border-slate-200",
                )}
              >
                {box.allocations.length > 0 ? (
                  box.allocations.map((allocation) => {
                    const sku = allocation.sku_id?.trim() ?? "";
                    return (
                      <TransferPackCartonAllocationRow
                        key={allocation.id}
                        allocation={allocation}
                        hint={sku !== "" ? skuHints[sku] : undefined}
                        quantityColumn={quantityColumn}
                        editing={editingAllocationId != null && String(editingAllocationId) === String(allocation.id)}
                        disabled={quantityMutationPending}
                        readOnly={box.sealed || !canPack}
                        onEdit={() => onEditAllocation(allocation)}
                        onDelete={() => onDeleteAllocation(String(allocation.id))}
                      />
                    );
                  })
                ) : (
                  <li className="px-2 py-2 text-xs text-slate-500">No lines on this carton yet.</li>
                )}
              </ul>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function TransferPackQuantityReadout({
  selectedSku,
  quantityDraft,
  kiosk = false,
  editingCartonLine = false,
  emptySelectionLabel = "Tap line or scan",
  onClearSelection,
  children,
}: {
  selectedSku: string | null;
  quantityDraft: string;
  kiosk?: boolean;
  editingCartonLine?: boolean;
  emptySelectionLabel?: string;
  onClearSelection?: () => void;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className={cn("rounded-lg border border-slate-300 bg-slate-50", kiosk ? "px-3 py-2" : "px-4 py-3")}>
      <div className="flex items-end justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Selected SKU</p>
          <p className={cn("truncate font-mono font-semibold text-slate-900", kiosk ? "text-sm" : "text-lg")}>
            {selectedSku ?? emptySelectionLabel}
          </p>
          {kiosk && editingCartonLine ? (
            <p className="mt-0.5 text-[11px] font-medium text-brand-800">Editing carton quantity</p>
          ) : null}
          {kiosk && selectedSku != null && onClearSelection != null ? (
            <button
              type="button"
              className="mt-0.5 text-[11px] font-medium text-brand-700 hover:text-brand-900"
              onClick={onClearSelection}
            >
              Clear
            </button>
          ) : null}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Qty</p>
          <p
            className={cn(
              "min-w-[3.5rem] font-bold tabular-nums leading-none text-slate-900",
              kiosk ? "text-3xl xl:text-4xl" : "text-5xl",
            )}
          >
            {quantityDraft === "" ? "0" : quantityDraft}
          </p>
        </div>
      </div>
      {children}
    </div>
  );
}

export function transferShipLessEligibleLines(
  transfer: Pick<TransferPackWorkspace, "all_boxes_sealed" | "lines">,
): TransferPackLine[] {
  if (!transfer.all_boxes_sealed) return [];
  return transfer.lines.filter(
    (line) =>
      line.allocated_quantity > 0
      && line.allocated_quantity < line.pack_target
      && !line.has_pending_pack_qty_request,
  );
}

const TRANSFER_PACK_SHIP_LESS_HOVER_HINT =
  "All cartons are sealed but some lines are still short. Request approval to ship the packed quantity.";

function TransferPackShipLessModal({
  open,
  lines,
  isSubmitting,
  onClose,
  onSubmit,
}: {
  open: boolean;
  lines: readonly TransferPackLine[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
}): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !isSubmitting) {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, isSubmitting, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      {...hubModalOverlayProps(true)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="transfer-pack-ship-less-modal-title"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) {
          onClose();
        }
      }}
    >
      <div className="my-4 w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <h2 id="transfer-pack-ship-less-modal-title" className="text-lg font-semibold text-slate-900">
            Ship with fewer components
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Request approval to ship the packed quantity for each line below.
          </p>
        </div>
        <ul className="max-h-[min(24rem,50vh)] divide-y divide-slate-100 overflow-y-auto px-4 sm:px-5">
          {lines.map((line) => {
            const sku = line.sku_id ?? `Line ${String(line.line_number ?? line.id)}`;
            return (
              <li key={line.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <span className="min-w-0 truncate font-mono text-slate-900">{sku}</span>
                <span className="shrink-0 tabular-nums text-slate-600">
                  packed {line.allocated_quantity} / target {line.pack_target}
                </span>
              </li>
            );
          })}
        </ul>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" disabled={isSubmitting} onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={isSubmitting} onClick={onSubmit}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Submitting…
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function TransferPackShipLessPanel({
  lines,
  disabled,
  isSubmitting,
  onSubmitAll,
}: {
  lines: readonly TransferPackLine[];
  disabled: boolean;
  isSubmitting: boolean;
  onSubmitAll: (lines: readonly TransferPackLine[]) => void | Promise<void>;
}): JSX.Element | null {
  const [modalOpen, setModalOpen] = useState(false);

  async function handleSubmit(): Promise<void> {
    try {
      await onSubmitAll(lines);
      setModalOpen(false);
    } catch {
      /* parent shows error toast */
    }
  }

  if (lines.length === 0) return null;

  return (
    <>
      <div
        className="flex shrink-0 items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1"
        role="region"
        aria-label="Ship with fewer components"
      >
        <div className="flex items-center gap-0.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-900">
            Ship with fewer
          </span>
          <HoverHintWithGlyph hoverHint={TRANSFER_PACK_SHIP_LESS_HOVER_HINT} />
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-8 shrink-0 whitespace-nowrap"
          disabled={disabled || isSubmitting}
          onClick={() => setModalOpen(true)}
        >
          Request ship incomplete
        </Button>
      </div>
      <TransferPackShipLessModal
        open={modalOpen}
        lines={lines}
        isSubmitting={isSubmitting}
        onClose={() => setModalOpen(false)}
        onSubmit={() => void handleSubmit()}
      />
    </>
  );
}
